import {
  ACCESS_LEVEL,
  AccessLevel,
  CORRECTIONS_ACCESS,
  Permissions,
  REFERENCE_PARTY_SECTIONS,
  ReferencePartyKind,
  SECTIONS,
  Section,
} from '@anessa/shared-models';
import { WEIGHBRIDGE_FILES } from '../../features/procurement/reference-file-view/weighbridge-files';

export type CommandPaletteAction =
  | 'open-bulk-timeseries-import'
  | 'open-add-dashboard';

export interface CommandPalettePage {
  labelKey: string;
  /**
   * Shown as-is instead of `labelKey`'s translation, for a label that is a name
   * rather than a word (the weighbridge files, ADEV-1207). `labelKey` then still
   * counts for search.
   */
  fixedLabel?: string;
  namespace: string;
  icon: string;
  /** Exactly one of `route`/`action` is set. `route` navigates there directly. */
  route?: string;
  /** `action` runs component-level behavior instead of navigating (e.g. opening a dialog). */
  action?: CommandPaletteAction;
  /** No permission means always visible (no permission guard on the route). */
  permission?: string | string[];
  /**
   * For a route gated by `data: { section: SECTIONS.X }` (Tier-1 section
   * access) rather than a flat `permission` capability — checked via
   * `PermissionService.canViewSection`, the same VIEW-level check the route
   * guard itself applies by default. Mirrors `route.data.section`, same as
   * `sidebar.ts`'s `NavItem.section`/`NavChild.section`. A page has at most
   * one of `permission`/`section` set; neither means always visible.
   */
  section?: Section;
  /** Level `section` needs (default view), mirroring the route's `data.sectionLevel`. */
  sectionLevel?: AccessLevel;
  /** The same level on any of these also grants it, mirroring the route's `data.orSections`. */
  orSections?: Section[];
  /**
   * Manual override for entries with no `route` (e.g. `action` entries), which can't be
   * looked up in `shared/nav/multi-plant-visibility.ts`. Entries with a `route` get their
   * multi-plant visibility from that shared registry automatically — don't set this for them.
   */
  hiddenForMultiPlant?: boolean;
  /** Same as `hiddenForMultiPlant` but for the opposite case — see that field's doc. */
  requiresMultiPlant?: boolean;
  /**
   * The parent sidebar group's label (e.g. 'approvisionnements'), shown as a
   * breadcrumb prefix so searching the group name (which isn't a page on its
   * own) still surfaces its sub-pages.
   */
  groupLabelKey?: string;
  groupNamespace?: string;
  /**
   * Extra translatable keys (resolved in `page.namespace`, same as `labelKey`)
   * that contribute to search matching but are never displayed — for terms a
   * user might search that don't appear in the page's own title. E.g. the
   * "Design" page (route `/parametrage/destinations`) is where on-site/off-site
   * destinations are configured, so someone searching "destinations" or
   * "on-site" should still find it.
   */
  searchKeys?: string[];
  /**
   * The page's French name, for a page the team often calls by its French name
   * (e.g. Gisements). Anessa staff in English see it right-aligned on the row
   * and can search it. Internal-only, so a fixed string rather than a Tolgee key,
   * like the `~` dev menu.
   */
  frenchName?: string;
}

/** Parent sidebar groups whose children are sub-pages here, keyed for reuse below. */
const GROUPS = {
  caracteristiquesSite: {
    groupLabelKey: 'nav_caracteristiques_site',
    groupNamespace: 'ui/sidebar',
  },
  // Sidebar V2 (ADEV-1046): "Données Site" (was Base de données Intrants) and
  // "Données entrantes" (the MotherDB browser, BDD Intrants, sync status).
  intrantsDb: {
    groupLabelKey: 'nav_donnees_site',
    groupNamespace: 'ui/sidebar',
  },
  donneesEntrantes: {
    groupLabelKey: 'nav_motherdb_views',
    groupNamespace: 'ui/sidebar',
  },
  // Données sortantes › Fichiers pont bascule (ADEV-1207).
  donneesSortantes: {
    groupLabelKey: 'nav_motherdb_outgoing_data',
    groupNamespace: 'ui/sidebar',
  },
  incorporations: {
    groupLabelKey: 'nav_incorporations',
    groupNamespace: 'ui/sidebar',
  },
  stocks: { groupLabelKey: 'nav_stocks', groupNamespace: 'ui/sidebar' },
  operationsSite: {
    groupLabelKey: 'nav_section_dashboards_operations',
    groupNamespace: 'ui/sidebar',
  },
  rationProduction: {
    groupLabelKey: 'nav_cible_ration_production',
    groupNamespace: 'ui/sidebar',
  },
} as const;

/**
 * A "Données Site" party page (Producteurs / Fournisseurs / Transporteurs): the
 * same unified page and section gate as the sidebar link (one section per kind,
 * ADEV-1207). (BDD
 * Intrants sits under Données entrantes, which has its own section, so it is
 * spelled out below.)
 */
const intrantsRef = (
  labelKey: string,
  kind: ReferencePartyKind,
  icon = 'badge',
): CommandPalettePage => ({
  labelKey,
  namespace: 'ui/sidebar',
  route: `/motherdb/reference-parties/${kind}`,
  icon,
  section: REFERENCE_PARTY_SECTIONS[kind],
  ...GROUPS.intrantsDb,
});

/**
 * Static registry of directly-navigable pages searched by the command palette.
 * Reuses existing `ui/sidebar` label keys where the sidebar already has one, so
 * a page's name stays consistent between the sidebar and the palette. Mirrors
 * the route/permission duplication already present across app.routes.ts and
 * sidebar.ts rather than introducing a new shared source of truth.
 *
 * Multi-plant visibility is the exception: entries with a `route` get it from
 * `shared/nav/multi-plant-visibility.ts`, the shared registry also consulted by
 * `sidebar.ts`, so the two can't drift apart. See `hiddenForMultiPlant`/
 * `requiresMultiPlant` above for the (route-less) manual-override case.
 */
export const COMMAND_PALETTE_PAGES: CommandPalettePage[] = [
  {
    labelKey: 'dashboards',
    namespace: 'ui/sidebar',
    route: '/dashboard',
    icon: 'dashboard',
    permission: [
      Permissions.VIEW_INDIVIDUAL_DASHBOARDS,
      Permissions.VIEW_SHARED_DASHBOARDS,
    ],
  },
  {
    labelKey: 'insights',
    namespace: 'ui/sidebar',
    route: '/insights',
    icon: 'psychology',
    permission: Permissions.VIEW_METRICS,
    // Labeled "Measures"/"Mesures", but still known by its route name and as metrics.
    searchKeys: ['search_key_insights', 'search_key_metrics'],
  },
  {
    labelKey: 'reports',
    namespace: 'ui/sidebar',
    route: '/reports',
    icon: 'description',
    permission: Permissions.VIEW_REPORTS,
  },
  {
    labelKey: 'flare_analysis',
    namespace: 'ui/sidebar',
    route: '/flare-analysis',
    icon: 'local_fire_department',
    permission: Permissions.VIEW_FLARE_ANALYSIS,
    searchKeys: ['search_key_torchage'],
    frenchName: 'Torchage',
  },
  {
    labelKey: 'custom_views',
    namespace: 'ui/sidebar',
    route: '/custom-views',
    icon: 'table_rows',
    permission: Permissions.VIEW_CUSTOM_VIEWS,
  },
  // Planning (Exploitation item). Section-gated, not capability-gated.
  {
    labelKey: 'nav_planning',
    namespace: 'ui/sidebar',
    route: '/planning',
    icon: 'event_note',
    section: SECTIONS.PLANNING,
  },
  {
    labelKey: 'approvisionnements',
    namespace: 'ui/sidebar',
    route: '/approvisionnements',
    icon: 'local_shipping',
    permission: Permissions.VIEW_RECEPTIONS,
    // Labeled "Reception", but also known as procurement / approvisionnements.
    searchKeys: ['search_key_procurement'],
    frenchName: 'Approvisionnements',
  },
  {
    labelKey: 'nav_expeditions',
    namespace: 'ui/sidebar',
    route: '/expeditions',
    icon: 'local_shipping',
    permission: Permissions.VIEW_EXPEDITIONS,
  },
  // Corrections console (ADEV-1075): Corrections at edit (ADEV-1207).
  {
    labelKey: 'nav_corrections',
    namespace: 'ui/sidebar',
    route: '/corrections',
    icon: 'edit_note',
    section: SECTIONS.CORRECTIONS,
    sectionLevel: CORRECTIONS_ACCESS.corrections,
  },
  // Digestat operator input (Operations item). Section-gated, not capability-gated.
  {
    labelKey: 'nav_digestat',
    namespace: 'ui/sidebar',
    route: '/digestat',
    icon: 'compost',
    section: SECTIONS.DIGESTAT,
  },
  // Operator checklist (Gestion des flux item, ADEV-1041). Section-gated.
  {
    labelKey: 'nav_checklist_operateur',
    namespace: 'ui/sidebar',
    route: '/checklist-operateur',
    icon: 'checklist',
    section: SECTIONS.CHECKLIST_OPERATEUR,
  },
  // RED II compliance report (Réglementaire & Conformité item). Section-gated.
  {
    labelKey: 'nav_red',
    namespace: 'ui/sidebar',
    route: '/red',
    icon: 'summarize',
    section: SECTIONS.RED,
  },
  // Suivi biologie operator input (Pilotage item). Section-gated.
  {
    labelKey: 'nav_suivi_biologie',
    namespace: 'ui/sidebar',
    route: '/suivi-biologie',
    icon: 'biotech',
    section: SECTIONS.SUIVI_BIOLOGIE,
    searchKeys: ['search_key_saisie_biologie'],
    frenchName: 'Saisie Biologie',
  },
  // Suivi ration weekly view (Pilotage item). Section-gated.
  {
    labelKey: 'nav_suivi_ration',
    namespace: 'ui/sidebar',
    route: '/suivi-ration',
    icon: 'donut_small',
    section: SECTIONS.SUIVI_RATION,
    searchKeys: ['search_key_suivi_ration'],
    frenchName: 'Suivi Ration',
  },
  {
    labelKey: 'nav_production',
    namespace: 'ui/sidebar',
    route: '/production',
    icon: 'factory',
    section: SECTIONS.PRODUCTION,
  },
  {
    labelKey: 'nav_ration_objectives',
    namespace: 'ui/sidebar',
    route: '/ration/objectives',
    icon: 'track_changes',
    section: SECTIONS.RATION_TYPE,
    ...GROUPS.rationProduction,
  },
  {
    labelKey: 'nav_ration_planning',
    namespace: 'ui/sidebar',
    route: '/ration-weekly-planning',
    icon: 'calendar_view_week',
    section: SECTIONS.RATION_PLANNING,
    ...GROUPS.rationProduction,
  },
  {
    labelKey: 'nav_ration_loading_requirement',
    namespace: 'ui/sidebar',
    route: '/ration/loading-requirement',
    icon: 'event_note',
    section: SECTIONS.RATION_PLANNING,
    ...GROUPS.rationProduction,
  },
  // Synoptique (Pilotage item). Section-gated.
  {
    labelKey: 'nav_synoptique',
    namespace: 'ui/sidebar',
    route: '/synoptique',
    icon: 'schema',
    section: SECTIONS.SYNOPTIQUE,
  },
  {
    labelKey: 'nav_energies_utilites',
    namespace: 'ui/sidebar',
    route: '/energies-utilites',
    icon: 'bolt',
    permission: Permissions.VIEW_ENERGIES_UTILITES,
  },
  {
    labelKey: 'nav_suivi_site',
    namespace: 'ui/sidebar',
    route: '/suivi-site',
    icon: 'monitoring',
    section: SECTIONS.SUIVI_SITE,
    searchKeys: ['search_key_suivi_site'],
    frenchName: 'Suivi site',
  },
  {
    labelKey: 'nav_suivi_transferts',
    namespace: 'ui/sidebar',
    route: '/suivi-transferts',
    icon: 'sync_alt',
    section: SECTIONS.SUIVI_TRANSFERTS,
    frenchName: 'Suivi des transferts',
  },
  // "Données Site" group (was "Base de données Intrants"): Gisements + the reference direct
  // links (each a specific MotherDB collection). The dynamic MotherDB Views/
  // Reference tree stays out of the palette (per-collection, dynamic).
  {
    labelKey: 'nav_gisements',
    namespace: 'ui/sidebar',
    route: '/matieres/gisements',
    icon: 'category',
    permission: Permissions.VIEW_GISEMENTS,
    // Labeled "Feedstocks" in English, but often called gisements there too.
    searchKeys: ['search_key_gisements'],
    frenchName: 'Gisements',
    ...GROUPS.intrantsDb,
  },
  // Données entrantes has its own section (ADEV-1192).
  {
    labelKey: 'nav_bdd_intrants',
    namespace: 'ui/sidebar',
    route: '/motherdb/collections/feedstock',
    icon: 'storage',
    section: SECTIONS.DONNEES_ENTRANTES,
    ...GROUPS.donneesEntrantes,
  },
  intrantsRef('nav_producteurs', 'producer'),
  intrantsRef('nav_fournisseurs', 'supplier'),
  intrantsRef('nav_transporteurs', 'transporter'),
  // Données sortantes › Fichiers pont bascule: one entry per file, labelled by
  // its filename; « Fichiers pont bascule » finds them all (ADEV-1207).
  ...WEIGHBRIDGE_FILES.map(
    (file): CommandPalettePage => ({
      labelKey: 'nav_weighbridge_files',
      fixedLabel: file.filename,
      namespace: 'ui/sidebar',
      route: file.route,
      icon: 'description',
      section: SECTIONS.DONNEES_SORTANTES,
      searchKeys: ['nav_weighbridge_files'],
      ...GROUPS.donneesSortantes,
    }),
  ),
  {
    labelKey: 'nav_conception',
    namespace: 'ui/sidebar',
    route: '/parametrage/destinations',
    icon: 'tune',
    permission: Permissions.VIEW_DESTINATIONS,
    searchKeys: [
      'search_key_conception_destinations',
      'search_key_conception_on_site',
      'search_key_conception_off_site',
      'search_key_conception_design',
      'search_key_conception_conception',
    ],
    ...GROUPS.caracteristiquesSite,
  },
  {
    labelKey: 'nav_contenant',
    namespace: 'ui/sidebar',
    route: '/parametrage/contenants',
    icon: 'tune',
    permission: Permissions.VIEW_CONTENANTS,
    ...GROUPS.caracteristiquesSite,
  },
  {
    labelKey: 'nav_assumptions',
    namespace: 'ui/sidebar',
    route: '/parametrage/assumptions',
    icon: 'rule_settings',
    searchKeys: ['search_biology_limits'],
    section: SECTIONS.ASSUMPTIONS,
    ...GROUPS.caracteristiquesSite,
  },
  {
    labelKey: 'nav_informations_site',
    namespace: 'ui/sidebar',
    route: '/parametrage/informations-site',
    icon: 'apartment',
    section: SECTIONS.INFORMATIONS_SITE,
    ...GROUPS.caracteristiquesSite,
  },
  {
    labelKey: 'nav_saisie',
    namespace: 'ui/sidebar',
    route: '/incorporations/saisie',
    icon: 'science',
    // Liquides and solides were separate pages; both words must still find
    // the merged one. Reuses the labels the two old entries carried.
    searchKeys: ['nav_saisie_liquides', 'nav_saisie_solides'],
    permission: Permissions.VIEW_INCORPORATIONS,
    ...GROUPS.incorporations,
  },
  // Its own item (no group) and section since sidebar V2 (ADEV-1046).
  {
    labelKey: 'nav_biodec',
    namespace: 'ui/sidebar',
    route: '/biodec',
    icon: 'science',
    // Labeled "Depackaging"/"Biodéconditionnement", but everyone calls it BIODEC.
    searchKeys: ['search_key_biodec'],
    section: SECTIONS.BIODEC,
  },
  {
    labelKey: 'nav_dilutions',
    namespace: 'ui/sidebar',
    route: '/incorporations/dilutions',
    icon: 'science',
    permission: Permissions.VIEW_INCORPORATIONS,
    ...GROUPS.incorporations,
  },
  {
    labelKey: 'nav_saisie_detail',
    namespace: 'ui/sidebar',
    route: '/incorporations/detail',
    icon: 'science',
    permission: Permissions.VIEW_INCORPORATIONS,
    ...GROUPS.incorporations,
  },
  {
    labelKey: 'nav_suivi_hebdo',
    namespace: 'ui/sidebar',
    route: '/incorporations/suivi-hebdomadaire',
    icon: 'science',
    permission: Permissions.VIEW_INCORPORATIONS,
    ...GROUPS.incorporations,
  },
  // Objectifs sat in Cible Ration & Production with its own section since
  // sidebar V2 (ADEV-1046); it's a top-level Pilotage item again (ADEV-1134).
  {
    labelKey: 'nav_objectives',
    namespace: 'ui/sidebar',
    route: '/objectives',
    icon: 'flag',
    section: SECTIONS.OBJECTIFS,
    // Labeled "Operating targets" in English; "objectives" should find it too.
    searchKeys: ['search_key_objectives'],
  },
  {
    labelKey: 'nav_stocks_composition',
    namespace: 'ui/sidebar',
    route: '/stocks/composition',
    icon: 'inventory_2',
    // The stock movements history is a block on this page, not a page of its own.
    searchKeys: ['search_key_stocks_movements'],
    permission: Permissions.VIEW_STOCKS,
    ...GROUPS.stocks,
  },
  {
    labelKey: 'nav_stocks_statut',
    namespace: 'ui/sidebar',
    route: '/stocks/statut',
    icon: 'inventory_2',
    permission: Permissions.VIEW_STOCKS,
    ...GROUPS.stocks,
  },
  // Operations - Site static boards. Each is gated by its own view-only board
  // section (dashboard_stocks_niveaux / dashboard_digestat / etc), not a
  // capability — the first two are ALSO capability-gated (they show
  // incorporation data), so they keep `permission` too.
  {
    labelKey: 'nav_incorporations_dashboard',
    namespace: 'ui/sidebar',
    route: '/dashboard/operations-site/incorporations',
    icon: 'bar_chart',
    section: SECTIONS.DASH_INCORPORATIONS,
    ...GROUPS.operationsSite,
  },
  {
    labelKey: 'nav_biodec_dashboard',
    namespace: 'ui/sidebar',
    route: '/dashboard/operations-site/biodec',
    icon: 'bar_chart',
    searchKeys: ['search_key_biodec'],
    section: SECTIONS.DASH_BIODEC,
    ...GROUPS.operationsSite,
  },
  {
    labelKey: 'nav_stocks_matieres_niveaux',
    namespace: 'ui/sidebar',
    route: '/dashboard/operations-site/stocks-niveaux',
    icon: 'bar_chart',
    section: SECTIONS.DASH_STOCKS_NIVEAUX,
    ...GROUPS.operationsSite,
  },
  {
    labelKey: 'nav_digestat',
    namespace: 'ui/sidebar',
    route: '/dashboard/operations-site/digestat',
    icon: 'compost',
    section: SECTIONS.DASH_DIGESTAT,
    ...GROUPS.operationsSite,
  },
  {
    labelKey: 'nav_biologie_qualite',
    namespace: 'ui/sidebar',
    route: '/dashboard/operations-site/biologie-qualite',
    icon: 'biotech',
    section: SECTIONS.DASH_BIOLOGIE_QUALITE,
    ...GROUPS.operationsSite,
  },
  {
    labelKey: 'nav_production_dashboard',
    namespace: 'ui/sidebar',
    route: '/dashboard/operations-site/production',
    icon: 'manufacturing',
    section: SECTIONS.DASH_PRODUCTION,
    ...GROUPS.operationsSite,
  },
  {
    labelKey: 'nav_energy_dashboard',
    namespace: 'ui/sidebar',
    route: '/dashboard/operations-site/energie',
    icon: 'bolt',
    ...GROUPS.operationsSite,
  },
  {
    labelKey: 'motherdb',
    namespace: 'ui/sidebar',
    route: '/motherdb',
    icon: 'hub',
    section: SECTIONS.DONNEES_ENTRANTES,
    ...GROUPS.donneesEntrantes,
  },
  {
    labelKey: 'nav_motherdb_sync_status',
    namespace: 'ui/sidebar',
    route: '/motherdb/sync-status',
    icon: 'hub',
    permission: Permissions.VIEW_MOTHERDB_SYNC_STATUS,
    ...GROUPS.donneesEntrantes,
  },
  {
    labelKey: 'data_management',
    namespace: 'ui/sidebar',
    route: '/data-management',
    icon: 'storage',
    permission: Permissions.VIEW_MANAGED_DATA,
  },
  {
    labelKey: 'timeseries_data',
    namespace: 'ui/sidebar',
    route: '/timeseries-data',
    icon: 'table_chart',
    permission: Permissions.VIEW_TIMESERIES,
  },
  // These five reuse labels from the topbar's user-avatar dropdown menu
  // (ui/topbar/dropdown-menu), which is their only other entry point today —
  // none of them appear in the sidebar.
  {
    labelKey: 'sensor_meta_editor_nav_btn',
    namespace: 'ui/topbar/dropdown-menu',
    route: '/sensor-meta-editor',
    icon: 'sensors',
    permission: Permissions.VIEW_SENSOR_META_EDITOR,
  },
  {
    labelKey: 'metrics_status_nav_btn',
    namespace: 'ui/topbar/dropdown-menu',
    route: '/metrics-status',
    icon: 'monitoring',
    permission: Permissions.RECOMPUTE_METRICS,
  },
  {
    labelKey: 'project_settings_nav_btn',
    namespace: 'ui/topbar/dropdown-menu',
    route: '/project-settings',
    icon: 'settings',
  },
  {
    labelKey: 'admin_option',
    namespace: 'ui/topbar/dropdown-menu',
    route: '/user-access-management',
    icon: 'admin_panel_settings',
    permission: Permissions.GLOBAL_ADMIN,
  },
  {
    labelKey: 'audit',
    namespace: 'ui/topbar/dropdown-menu',
    route: '/audit',
    icon: 'fact_check',
    permission: Permissions.GLOBAL_ADMIN,
  },
  // Only reachable today via a button inside the user-access-management page.
  {
    labelKey: 'manage_roles',
    namespace: 'features/permission-management/user-access-management',
    route: '/role-permissions-management',
    icon: 'security',
    permission: Permissions.GLOBAL_ADMIN,
  },
  {
    labelKey: 'invitations_title',
    namespace: 'features/permission-management/invitations',
    route: '/invitations',
    icon: 'person_add',
    permission: Permissions.GLOBAL_ADMIN,
  },
  {
    labelKey: 'project_selection_title',
    namespace: 'features/project-selection',
    route: '/plants',
    icon: 'apartment',
  },
  // Opens the MultiTabImportDialog directly instead of navigating — it's a
  // dialog, not a routed page, reached today via the topbar dropdown menu
  // (same permission/multi-plant gating as that entry point).
  {
    labelKey: 'import_export_nav_btn',
    namespace: 'ui/topbar/dropdown-menu',
    action: 'open-bulk-timeseries-import',
    icon: 'file_present',
    permission: Permissions.EDIT_TIMESERIES,
    hiddenForMultiPlant: true,
  },
  // Same gate as the sidebar's "+" (edit on Dashboards); with no project open
  // the section level is none, so it's hidden then too.
  {
    labelKey: 'title',
    namespace: 'ui/dashboard/add-dashboard-dialog',
    action: 'open-add-dashboard',
    icon: 'add_chart',
    section: SECTIONS.DASHBOARDS,
    sectionLevel: ACCESS_LEVEL.EDIT,
  },
];

/**
 * Every foreign i18n namespace a page/group entry above borrows a label from,
 * deduped. `CommandPalette` loads these on construction (i.e. whenever the
 * palette dialog opens) so its labels resolve; `Layout` also prefetches them
 * eagerly at app-shell mount so they're normally already warm by the time the
 * user first opens the palette — see `layout.ts`.
 */
export const COMMAND_PALETTE_NAMESPACES = Array.from(
  new Set(
    COMMAND_PALETTE_PAGES.flatMap((page) =>
      page.groupNamespace
        ? [page.namespace, page.groupNamespace]
        : [page.namespace],
    ),
  ),
);
